import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
} from '@mui/material';

import { authService } from '../../services/authService';
import type { User } from '../../types';

/**
 * Настройки пользователя: профиль и безопасность.
 * Первая простая версия – только базовые поля.
 */

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [profileForm, setProfileForm] = useState<Partial<User>>({});
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
  });
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    const loadUser = async () => {
      // Используем /auth/me через api
      const { api } = await import('../../services/api');
      try {
        setLoading(true);
        const current = await api.getCurrentUser(); // запрос /auth/profile GET пока нет, обходимся /auth/me через api
        setUser(current);
        setProfileForm({
          first_name: (current as any).first_name,
          last_name: (current as any).last_name,
          email: current.email,
        });
      } catch (e: any) {
        setSnackbar({ message: e.message || 'Failed to load profile', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const handleProfileSave = async () => {
    try {
      setSaving(true);
      const updated = await authService.updateProfile(profileForm);
      setUser(updated);
      setSnackbar({ message: t('profile.saved'), severity: 'success' });
    } catch (e: any) {
      setSnackbar({ message: e.message || 'Error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setSaving(true);
      await authService.changePassword(passwordForm);
      setPasswordForm({ current_password: '', new_password: '' });
      setSnackbar({ message: t('profile.passwordChanged'), severity: 'success' });
    } catch (e: any) {
      setSnackbar({ message: e.message || 'Error', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('navigation.settings')}
      </Typography>

      <Grid container spacing={4}>
        {/* Профиль */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('profile.profileInfo')}
            </Typography>
            <TextField
              fullWidth
              label={t('profile.firstName')}
              margin="normal"
              value={profileForm.first_name || ''}
              onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              label={t('profile.lastName')}
              margin="normal"
              value={profileForm.last_name || ''}
              onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
            />
            <TextField
              fullWidth
              label={t('profile.email')}
              margin="normal"
              value={profileForm.email || ''}
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleProfileSave} disabled={saving}>
                {t('common.save')}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Смена пароля */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('profile.changePassword')}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label={t('profile.currentPassword')}
              margin="normal"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label={t('profile.newPassword')}
              margin="normal"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handlePasswordChange} disabled={saving}>
                {t('profile.updatePassword')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
